/**
 * SetBlockConversationUseCase
 *
 * Applies the requested block/un-block state for a conversation with `peerId`
 * following Flutter's optimistic-write-then-rollback orchestration:
 *   1. Optimistic local write FIRST (for instant UI — Flutter notes the user
 *      "could reopen chat actions before saving on server").
 *   2. Call the server hop via `chatService.setBlockConversation`.
 *   3. Roll the local write back (the exact inverse) if the server fails.
 *
 * Unlike `BlockUserUseCase`, this use case does NOT toggle: the `block` flag is
 * supplied by the caller. Flutter computes `willBlock = !_conversationBlocked`
 * at the sheet level (Task 3 owns that derivation); the repository simply
 * applies the requested state.
 *
 * Flutter source: lib/data/repository/chat_repository.dart:27-37
 *   (`setBlockConversation` — local write → server → rollback on `!success`)
 *   and :73-82 (`_setHiveBlocConversation` — `put` on block, `delete` on
 *   un-block).
 *
 * Storage-key divergence (deliberate): Flutter's `_setHiveBlocConversation`
 * writes the local record into the shared `BlockedUser` Hive box, but the web
 * keeps conversation-block state under the SEPARATED
 * `SECURE_STORAGE_KEYS.BLOCKED_CONVERSATIONS + peerId` key — the exact key
 * `IsConversationBlockedUseCase` reads
 * (isConversationBlockedUseCase.ts:36). The optimistic local write MUST target
 * `BLOCKED_CONVERSATIONS` so the write and the predicate read stay consistent.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { chatService } from '@/domain/services/chat/chatService';
import type { BlockedConversationStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class SetBlockConversationUseCase {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Apply the requested block state for the conversation with `peerId`.
   *
   * @param peerId - The peer user's Firebase id.
   * @param block - `true` to block, `false` to un-block. Supplied by the caller
   *   (no toggling happens here).
   * @returns `true` on success, `false` if the server write fails or anything
   *   throws (the local write is rolled back to its pre-call state on failure).
   */
  async execute(peerId: string, block: boolean): Promise<boolean> {
    const storageKey = SECURE_STORAGE_KEYS.BLOCKED_CONVERSATIONS + peerId;

    try {
      // Step 1: Optimistic local write FIRST (for instant UI feedback —
      // parity with chat_repository.dart:28-29). `block` -> write the record,
      // `!block` -> delete it (chat_repository.dart:76-81).
      if (block) {
        const record: BlockedConversationStorage = { firebaseId: peerId };
        await this.storage.setEncrypted(storageKey, record);
      } else {
        await this.storage.remove(storageKey);
      }

      // Step 2: Server hop.
      const success = await chatService.setBlockConversation(peerId, block);

      // Step 3: Roll the local write back on failure — the exact inverse of the
      // optimistic write, matching Flutter's `_setHiveBlocConversation(firebaseId, !block)`
      // (chat_repository.dart:32-34).
      if (!success) {
        if (block) {
          await this.storage.remove(storageKey);
        } else {
          const record: BlockedConversationStorage = { firebaseId: peerId };
          await this.storage.setEncrypted(storageKey, record);
        }
        return false;
      }

      return true;
    } catch (error) {
      appLogger.error('Error setting block conversation', 'SetBlockConversationUseCase.execute', {
        error: error as Error,
      });
      return false;
    }
  }
}

export const setBlockConversationUseCase = new SetBlockConversationUseCase();
